import { useState, useEffect, useCallback, type ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { ResizeHandle, TOUCH_QUERY, useMediaQuery } from "@bastion/ui";
import { ChatView } from "../ChatView";
import { ChatInput } from "../ChatInput";
import type { Conversation } from "../hooks";
import type { ChatMessage } from "../types";
import styles from "./AssistantWorkspace.module.css";

/**
 * Where the chat and the dashboard preview share the height
 * (AssistantWorkspace.module.css): a phone, and a touch screen up to 1100px
 * wide. The preview is full width there, so it has no resize handle.
 */
const ARTIFACT_SPLIT_QUERY = "(max-width: 760px), (pointer: coarse) and (max-width: 1100px)";

export interface AssistantWorkspaceProps {
  conversations?: Conversation[];
  isLoadingConversations?: boolean;
  selectedSessionId?: string | null;
  onSelectConversation?: (conversation: Conversation) => void;
  onNewConversation?: () => void;
  onStartConversation: (prompt: string) => void;
  onDeleteConversation?: (sessionId: string) => void;
  /** @deprecated Conversation list lives in the app shell; refresh is unused. */
  onRefreshConversations?: () => void;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  onSendMessage: (content: string) => void;
  onStopStreaming?: () => void;
  onConfirmToolCall?: (toolCallId: string) => void;
  onCancelToolCall?: (toolCallId: string) => void;
  /** Reply to a custom tool renderer (e.g. ChoiceCard pick). */
  onAnswerToolCall?: (toolCallId: string, answerText: string) => void;
  welcomeEyebrow?: string;
  welcomeTitle: string;
  welcomeDescription?: string;
  welcomeNote?: string;
  welcomePlaceholder?: string;
  welcomeSupplementaryContent?: ReactNode;
  activeConversationTitle?: string;
  activeConversationSummary?: string | null;
  chatPlaceholder?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  /**
   * Optional right-side artifact panel content. Renders alongside the
   * chat surface (split layout) when present, hidden otherwise. The
   * webapp uses this to surface in-flight dashboard previews while the
   * agent is streaming widgets via `propose_widget` / `propose_dashboard`.
   * Only shown once a conversation is active (`selectedSessionId`).
   */
  rightPanel?: ReactNode;
}

export function AssistantWorkspace({
  conversations: _conversations,
  isLoadingConversations: _isLoadingConversations = false,
  selectedSessionId,
  onSelectConversation: _onSelectConversation,
  onNewConversation: _onNewConversation,
  onStartConversation,
  onDeleteConversation,
  onRefreshConversations: _onRefreshConversations,
  messages,
  isStreaming,
  error,
  onSendMessage,
  onStopStreaming,
  onConfirmToolCall,
  onCancelToolCall,
  onAnswerToolCall,
  welcomeEyebrow: _welcomeEyebrow = "Parametric × Bastion",
  welcomeTitle,
  welcomeDescription: _welcomeDescription,
  welcomeNote: _welcomeNote,
  welcomePlaceholder = "Ask Gauss to summarize your work, triage data, or start a new thread…",
  welcomeSupplementaryContent,
  activeConversationTitle: _activeConversationTitle = "New conversation",
  activeConversationSummary: _activeConversationSummary,
  chatPlaceholder = "Continue the conversation…",
  emptyStateTitle = "Gauss",
  emptyStateDescription = "Ask about your data, collection workflows, or anything happening in Bastion.",
  rightPanel,
}: AssistantWorkspaceProps) {
  const showConversation = Boolean(selectedSessionId);
  const hasMessages = messages.length > 0;
  const showThread = showConversation && hasMessages;
  const showWelcome = !showThread;
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // On a touch screen the home field does not take focus at load, so the
  // keyboard does not open over the suggestions.
  const isTouch = useMediaQuery(TOUCH_QUERY);
  const isArtifactSplit = useMediaQuery(ARTIFACT_SPLIT_QUERY);

  useEffect(() => {
    if (!isDeleteConfirmOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsDeleteConfirmOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDeleteConfirmOpen]);

  const handleConfirmDelete = useCallback(() => {
    if (selectedSessionId && onDeleteConversation) {
      onDeleteConversation(selectedSessionId);
    }
    setIsDeleteConfirmOpen(false);
  }, [selectedSessionId, onDeleteConversation]);

  const showArtifact = Boolean(rightPanel) && showThread;
  const [artifactWidth, setArtifactWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 560;
    const stored = window.localStorage.getItem("assistant.artifactPanel.width");
    if (!stored) return 560;
    const parsed = Number.parseInt(stored, 10);
    return Number.isFinite(parsed)
      ? Math.max(360, Math.min(1200, parsed))
      : 560;
  });

  const persistArtifactWidth = useCallback((width: number) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      "assistant.artifactPanel.width",
      String(Math.round(width)),
    );
  }, []);

  return (
    <div className={`${styles.shell} ${showArtifact ? styles.shellWithArtifact : ""}`}>
      <section className={styles.main}>
        <div className={styles.welcomeLayout}>
          {showWelcome ? (
            <div className={styles.welcomeStage}>
              <div className={styles.welcomePanel}>
                <div className={styles.welcomeInner}>
                  <h1 className={styles.welcomeTitle}>{welcomeTitle}</h1>

                  <div className={styles.heroInput}>
                    <ChatInput
                      variant="hero"
                      autoFocus={!isTouch}
                      onSend={onStartConversation}
                      placeholder={welcomePlaceholder}
                    />
                  </div>

                  {welcomeSupplementaryContent ? (
                    <div className={styles.supplementary}>
                      {welcomeSupplementaryContent}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.threadStage}>
              {onDeleteConversation ? (
                <div className={styles.threadToolbar}>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => setIsDeleteConfirmOpen(true)}
                    title="Delete conversation"
                    aria-label="Delete conversation"
                  >
                    <Trash2 className={styles.deleteButtonIcon} />
                  </button>
                </div>
              ) : null}
              <ChatView
                className={styles.chatView}
                messages={messages}
                isStreaming={isStreaming}
                error={error}
                onSendMessage={onSendMessage}
                onStopStreaming={onStopStreaming}
                onConfirmToolCall={onConfirmToolCall}
                onCancelToolCall={onCancelToolCall}
                onAnswerToolCall={onAnswerToolCall}
                inputPlaceholder={chatPlaceholder}
                emptyStateTitle={emptyStateTitle}
                emptyStateDescription={emptyStateDescription}
              />
            </div>
          )}
        </div>
      </section>

      {showArtifact ? (
        <aside className={styles.artifactPanel} style={{ width: artifactWidth }}>
          {isArtifactSplit ? null : (
            <ResizeHandle
              side="left"
              minWidth={360}
              maxWidth={1200}
              onResize={setArtifactWidth}
              onResizeEnd={persistArtifactWidth}
              onDoubleClick={() => {
                setArtifactWidth(560);
                persistArtifactWidth(560);
              }}
            />
          )}
          {rightPanel}
        </aside>
      ) : null}

      {isDeleteConfirmOpen ? (
        <div
          className={styles.modalBackdrop}
          onClick={() => setIsDeleteConfirmOpen(false)}
          data-testid="delete-confirm-backdrop"
        >
          <div
            className={styles.deleteDialog}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className={styles.deleteDialogTitle}>Delete conversation?</h2>
            <p className={styles.deleteDialogDescription}>
              This will permanently remove this conversation and its messages. This action cannot be undone.
            </p>
            <div className={styles.deleteDialogActions}>
              <button
                type="button"
                className={styles.deleteDialogCancel}
                onClick={() => setIsDeleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.deleteDialogConfirm}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
